import HeadData from 'components/Common/HeadData'
import LayoutContainer from 'components/Common/LayoutContainer'
import Pane from 'components/Common/Pane'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import TextButton from 'LuxKit/Button/TextButton'
import Group from 'components/utils/Group'
import LineBedIcon from 'LuxKit/Icons/line/LineBedIcon'
import UltraLuxLogo from 'LuxKit/Label/UltraLuxLogo'
import TabButton from 'LuxKit/Tabs/TabButton'
import TabButtonsGroup from 'LuxKit/Tabs/TabButtonsGroup'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import HotelSearchPageFilters from 'components/Pages/HotelSearchPage/HotelSearchPageFilters'
import OfferList from 'components/OfferList/OfferList'
import { OfferListEventsProvider } from 'components/OfferList/OfferListEventsContext'
import { mediaQueryUp } from 'components/utils/breakpoint'
import config from 'constants/config'
import useOfferListSortOptions from 'hooks/useOfferListSortOptions'
import ProductPaletteProvider from 'contexts/ProductPaletteContext'
import useOffer from 'hooks/Offers/useOffer'
import useOfferList from 'hooks/Offers/useOfferList'
import switchFunc from 'lib/function/switchFunc'
import { rem } from 'polished'
import { useContext, useEffect, useMemo, useRef, useState, useCallback } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import { connect } from 'react-redux'
import { getUrlOfferListFilters } from 'selectors/routerSelectors'
import styled from 'styled-components'
import UltraLuxVerticalPageUSPs from './UltraLuxVerticalPageUSPs'
import zIndex from 'styles/tools/z-index'
import LineCompassIcon from 'LuxKit/Icons/line/LineCompassIcon'
import { InView } from 'react-intersection-observer'
import { useLocation } from 'react-router-dom'
import debounce from 'debounce-promise'
import LineShipIcon from 'LuxKit/Icons/line/LineShipIcon'
import GeoContext from 'contexts/geoContext'
import { isEnabledForFeature } from 'lib/config/featureFlagUtils'
import PromptCardSelector from 'components/Common/StickyPromptCard/PromptCardSelector'
import { SearchBurnProvider } from 'contexts/SearchPointsContext'
import AnalyticsPageContext, { AnalyticsPage } from 'contexts/Analytics/analyticsPageContext'
import ErrorBoundary from 'components/Common/ErrorBoundary/ErrorBoundary'
import { isManualSortingEnabledFor } from 'selectors/featuresSelectors'
import { getListName } from 'analytics/enhanced-ecommerce'
import useOfferListTracking from 'hooks/Offers/useOfferListTracking'
import { ANYWHERE_PLACE_ID, ANYWHERE_NAME } from 'constants/search'
import getOfferListKey from 'lib/offer/offerListKey'
import type { FilterControlChangeHandler } from 'components/Pages/HotelSearchPage/HotelSearchPageFilterPane'
import { replace } from 'lib/routing/appRouting'
import { useAppDispatch } from 'hooks/reduxHooks'
import type { SortControlChangeHandler } from 'components/Common/OfferSortControl/OfferSortControl'

const videoUrl = 'https://player.vimeo.com/progressive_redirect/playback/782405070/rendition/1080p/file.mp4?loc=external&signature=d2871cdd822bba9a89ad7436fe54dd2d7f3defa73b336eec2ef3d4afe4ebf091'

const UltraluxVertical = styled.div`
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
`

const OffersSection = styled(VerticalSpacer)`
  padding-top: ${rem(20)};
  padding-bottom: ${rem(32)};
`

const HeroContent = styled(LayoutContainer)`
  display: flex;
  height: 100%;
  width: 100%;
  color: ${props => props.theme.palette.neutral.surface.darkContrast};
  position: absolute;
  inset: 0;
  align-items: center;
  justify-content: center;
`
const UltraluxImageBanner = styled.div`
  height: ${rem(350)};
  position: relative;

  ${mediaQueryUp.tablet} {
    height: ${rem(400)};
  }
`

const VerticalSelectorWrapper = styled.div`
  position: sticky;
  top: 0;
  margin-inline: ${rem(-12)};
  z-index: ${zIndex.stickyHeader};

  ${mediaQueryUp.tablet} {
    position: absolute;
    top: ${rem(20)};
    margin: 0;
    padding: 0 ${rem(12)};
    display: flex;
    justify-content: center;
    /* oxlint-disable-next-line logical-css/no-physical-properties */
    left: 50%;
    transform: translateY(-100%) translateX(-50%);
    transition: transform 0.3s, top 0.3s;
    z-index: 1;

    &.full-size {
      position: fixed;
      top: 0;
      transform: translateY(0) translateX(-50%);
      z-index: ${zIndex.stickyHeader};
    }
  }
`

const VerticalSelector = styled(Pane)`
  position: absolute;
  inset: 0;

  ${mediaQueryUp.tablet} {
    box-shadow: ${props => props.theme.shadow.bottom.medium};
    border-radius: 2px;
    transform: scaleX(1);
    transition: transform 0.3s;

    &.full-size {
      transition: transform 0.3s;
      transform: scaleX(var(--menu-scale, 1));
    }
  }
`

const UltraluxHeroVideo = styled.video`
  height: 100%;
  width: 100%;
  object-fit: cover;
`

interface Props {
  filters: App.OfferListFilters
}

type UltraLuxVerticals = 'hotels' | 'tours' | 'cruises'

interface VerticalData {
  id: UltraLuxVerticals
  offerTypes: Array<App.OfferListOfferType>
  filters: App.OfferListFilters
  filtersUnifiedEndpoint: App.OfferListFilters
  title: string
  verticalPageUrl: string
}

const hotelConfig: VerticalData = {
  id: 'hotels',
  offerTypes: ['hotel', 'tactical_ao_hotel'],
  title: 'hotels',
  filters: { propertySubCategories: ['Ultra Lux'] },
  filtersUnifiedEndpoint: {},
  verticalPageUrl: '/hotels',
}

const tourConfig: VerticalData = {
  id: 'tours',
  offerTypes: ['tour_v2'],
  title: 'tours',
  filters: { productTypes: ['ultra_lux_tour'] },
  filtersUnifiedEndpoint: {},
  verticalPageUrl: '/tours',
}

const cruiseConfig: VerticalData = {
  id: 'cruises',
  offerTypes: ['tour'],
  title: 'cruises',
  filters: { productTypes: ['ultra_lux_tour'], holidayTypes: ['Cruises'] },
  filtersUnifiedEndpoint: { holidayTypes: ['Cruises'] },
  verticalPageUrl: '/cruises',
}

const verticals = switchFunc<VerticalData, string>({
  hotels: hotelConfig,
  tours: tourConfig,
  cruises: cruiseConfig,
},
// default back to hotel
hotelConfig)

function UltraLuxVerticalPage(props: Props) {
  const { filters } = props
  const intl = useIntl()
  const { frontPageSortByTypes } = useOfferListSortOptions()
  const [showFullSizeBar, setShowFullSizeBar] = useState<boolean>()
  const { currentRegionCode } = useContext(GeoContext)
  const loc = useLocation()
  const hash = loc.hash.replace('#', '')
  const vertical = verticals(hash)

  const isManualSortingEnabled = isManualSortingEnabledFor('ultralux')

  const finalFilters = useMemo((): App.OfferListFilters => {
    // Unified endpoint is a shallow endpoint and doesn't support sortBy filter, it's neccessary to use search endpoint instead.
    if (isManualSortingEnabled && !filters.sortBy) {
      const inheritedFilters = {
        ...filters,
        ...vertical.filtersUnifiedEndpoint,
      }

      return {
        ...inheritedFilters,
        offerTypes: vertical.offerTypes,
        sortBy: 'vertical-ultralux',
        holidayTypesScoped: ['Ultra Lux'],
        limit: 250,
        destinationId: ANYWHERE_PLACE_ID,
      }
    }

    return {
      ...filters,
      ...vertical.filters,
      offerTypes: vertical.offerTypes,
      frontPageVisibleOnly: true,
      sortBy: filters.sortBy ?? 'homepage',
      destinationId: ANYWHERE_PLACE_ID,
    }
  }, [filters, isManualSortingEnabled, vertical.filters, vertical.filtersUnifiedEndpoint, vertical.offerTypes])

  const tracking = useOfferListTracking(
    getListName('OfferList', 'ULTRALUX', 'Ultralux Page Offer List'),
    { key: getOfferListKey(finalFilters) },
  )

  const offerList = useOfferList(finalFilters)
  const [firstOffer] = useOffer<App.Offer>(offerList.offerIds[0])
  const menuRef = useRef<HTMLDivElement | null>(null)
  const [scale, setScale] = useState<number>()

  const dispatch = useAppDispatch()

  const handleFilterChange = useCallback<FilterControlChangeHandler>((params) => {
    dispatch(replace({
      search: params.search,
      hash: params.hash,
    }))
  }, [dispatch])

  const handleSortChange = useCallback<SortControlChangeHandler>((params) => {
    dispatch(replace({
      search: params.search,
    }))
    window.scrollTo({ behavior: 'instant', top: 0 })
  }, [dispatch])

  const mapViewURL = `/search/map?adults=2&children=none&destinationId=${ANYWHERE_PLACE_ID}&destinationName=${ANYWHERE_NAME}`

  const switchView = {
    label: intl.formatMessage({
      id: 'XOgh0sNB2uFYVbuF1Dyp',
      defaultMessage: 'Map view',
      description: 'Label for map view toggle on Ultra Lux page on the map view',
    }),
    view: 'map' as const,
    URL: mapViewURL,
  }

  useEffect(() => {
    const calcScale = () => {
      setScale(window.innerWidth / (menuRef.current?.offsetWidth ?? 1))
    }
    const debouncedCalc = debounce(calcScale, 50)
    window.addEventListener('resize', debouncedCalc)
    calcScale()

    return () => window.removeEventListener('resize', debouncedCalc)
  }, [])

  const cruisesEnabled = useMemo(() => isEnabledForFeature(config.CRUISES_REGIONS, currentRegionCode), [currentRegionCode])

  const heroHeadingText = cruisesEnabled
    ? intl.formatMessage({
        id: '6E2shNohitnWjp1QaUno',
        defaultMessage: 'Experience the world’s most luxurious hotels, tours & cruises',
        description: 'Ultra Lux hero heading when cruises are enabled on the offer types landing page',
      })
    : intl.formatMessage({
        id: 'HRZDW4zOH1Gj1Y6T5ibp',
        defaultMessage: 'Experience the world’s most luxurious hotels & tours',
        description: 'Ultra Lux hero heading when cruises are disabled on the offer types landing page',
      })

  return <AnalyticsPageContext.Provider value={AnalyticsPage.verticalPage}>
    <UltraluxVertical>
      <HeadData
        title={intl.formatMessage({
          id: '4O5Hask05F97yPwdQXAQ',
          defaultMessage: 'Ultra Lux by Luxury Escapes',
          description: 'Page title for the Ultra Lux vertical page on the offer types landing page',
        })}
        description={intl.formatMessage({
          id: 'BCqW_RxOgzlZ-amNW0FW',
          defaultMessage: 'Experience the world’s most luxurious stays. Exceptional 5 & 6 star properties. Browse all offers now.',
          description: 'Meta description for Ultra Lux vertical page for the experience',
        })}
        imageId={firstOffer?.image?.id}
        imageAlt={firstOffer?.image?.title}
      />
      <ErrorBoundary fallback={null}>
        <PromptCardSelector tripReengagement stickyTop={56} />
      </ErrorBoundary>
      <UltraluxImageBanner>
        <UltraluxHeroVideo
          controls={false}
          autoPlay
          playsInline
          loop
          muted
        >
          <source
            src={videoUrl}
            type="video/mp4"
          />
        </UltraluxHeroVideo>
        <HeroContent>
          <Group direction="vertical" horizontalAlign="center" gap={24}>
            <UltraLuxLogo height={28} />
            <Heading variant="heading3alt" colour="dark-contrast">
              {heroHeadingText}
            </Heading>
          </Group>
        </HeroContent>
      </UltraluxImageBanner>
      <LayoutContainer relative>
        <VerticalSelectorWrapper className={showFullSizeBar ? 'full-size' : undefined}>
          <VerticalSelector style={{ '--menu-scale': scale }} ref={menuRef} className={showFullSizeBar ? 'full-size' : undefined} />
          <TabButtonsGroup>
            <TabButton
              size="medium"
              isActive={vertical.id === 'hotels'}
              href="#hotels"
              startIcon={<LineBedIcon />}
            >
              {intl.formatMessage({
                id: 'OM2BV4EaqgAYx4SVEI3E',
                defaultMessage: 'Ultra Lux Hotels',
                description: 'Tab label for Ultra Lux Hotels vertical on the hotel or room booking page',
              })}
            </TabButton>
            <TabButton
              size="medium"
              isActive={vertical.id === 'tours'}
              href="#tours"
              startIcon={<LineCompassIcon />}
            >
              {intl.formatMessage({
                id: 'RKSSmJtkW8VG7NDYzILA',
                defaultMessage: 'Ultra Lux Tours',
                description: 'Tab label for Ultra Lux Tours vertical on the tour offer detail page',
              })}
            </TabButton>
            {cruisesEnabled && <TabButton
              size="medium"
              isActive={vertical.id === 'cruises'}
              startIcon={<LineShipIcon />}
              href="#cruises"
            >
              {intl.formatMessage({
                id: '2ds6qkiitZc8rUyeYu41',
                defaultMessage: 'Ultra Lux Cruises',
                description: 'Tab label for Ultra Lux Cruises vertical in the cruise section',
              })}
            </TabButton>}
          </TabButtonsGroup>
        </VerticalSelectorWrapper>
        <InView rootMargin="20px" onChange={(inView: boolean) => setShowFullSizeBar(!inView)} />
        <UltraLuxVerticalPageUSPs verticalType={vertical.id} />
        <OffersSection id="offers" gap={40}>
          <SearchBurnProvider>
            <div>
              {vertical.id !== 'hotels' && <Heading variant="heading2" align="start">
                {intl.formatMessage(
                  {
                    id: 'x_3Pa8Vyq4v6W5wXiDlS',
                    defaultMessage: 'Explore our <i>ultra lux</i> {verticalTitle}',
                    description: 'Heading for Ultra Lux vertical offer list on the offer page',
                  },
                  {
                    i: (chunks: React.ReactNode) => <i key="italic">{chunks}</i>,
                    verticalTitle: vertical.title,
                  },
                )}
              </Heading>}
              {vertical.id === 'hotels' && <HotelSearchPageFilters
                heading={intl.formatMessage(
                  {
                    id: 'x_3Pa8Vyq4v6W5wXiDlS',
                    defaultMessage: 'Explore our <i>ultra lux</i> {verticalTitle}',
                    description: 'Heading for Ultra Lux vertical offer list on the offer page',
                  },
                  {
                    i: (chunks: React.ReactNode) => <i key="italic">{chunks}</i>,
                    verticalTitle: vertical.title,
                  },
                )}
                filters={finalFilters}
                sortOptions={frontPageSortByTypes}
                switchView={switchView}
                shouldShowFilters
                onFilterChange={handleFilterChange}
                onSortChange={handleSortChange}
              />}
            </div>
            <ProductPaletteProvider palette="ultralux">
              <OfferListEventsProvider tracking={tracking}>
                <OfferList
                  filters={finalFilters}
                  interstitials={[{
                    id: 'noResults',
                    show: 'hasNoOffers',
                    position: 'start',
                    element: <VerticalSpacer gap={40}>
                      <Heading variant="heading4">{intl.formatMessage(
                        {
                          id: 'JerEi04X-LLdseuwEqk6',
                          defaultMessage: 'Unfortunately, there are no Ultra Lux {verticalTitle} available at this time.',
                          description: 'No results heading when no Ultra Lux offers are available',
                        },
                        { verticalTitle: vertical.title },
                      )}
                      </Heading>
                      <BodyText variant="large">
                        {intl.formatMessage(
                          {
                            id: 'DY-t7s19ye6J8z9TEZmJ',
                            defaultMessage: 'Check out our other {verticalTitle} collection for more options or call our customer service team 24 hours, 7 days for personalised recommendations.',
                            description: 'No results body text suggesting alternative Ultra Lux options',
                          },
                          { verticalTitle: vertical.title },
                        )}
                      </BodyText>
                      <TextButton kind="primary" to={vertical.verticalPageUrl}>
                        {intl.formatMessage(
                          {
                            id: 'bTwyvH0Qpf2skAoV_yUX',
                            defaultMessage: 'View more {verticalId} options',
                            description: 'Button to view more options for a specific Ultra Lux vertical',
                          },
                          { verticalId: vertical.id },
                        )}
                      </TextButton>
                    </VerticalSpacer>,
                  }]}
                />
              </OfferListEventsProvider>
            </ProductPaletteProvider>
          </SearchBurnProvider>
        </OffersSection>
      </LayoutContainer>
    </UltraluxVertical>
  </AnalyticsPageContext.Provider>
}

function mapStateToProps(state: App.State) {
  return {
    filters: getUrlOfferListFilters(state),
  }
}

export default connect(mapStateToProps)(UltraLuxVerticalPage)
